// fetchFeedTotalCount.js
import getResourceUrl from './getResourceUrl.js';
import getFeedName from '../selector/getFeedName.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getSelectedQueryOptionObject } from '../selector/getFeedQueryObject.js';
import getSelectedFeedQueryOptions from '../selector/getSelectedFeedQueryOptions.js';
import { QueryPrefix, QueryType } from './feedConstants.js';
import {
  ProfileListType,
  PROFILE_FEED_REMOTE_CONFIG_KEY_MAP,
} from './profileConstants.js';

/**
 * Get the feed name with query for a user's feed
 * @param {Object} state - Redux state
 * @param {string} userId - User ID
 * @param {string} listType - List type (e.g., 'flix', 'image', 'story', 'short', 'post')
 * @returns {string|null} Feed name with query, or null if not found
 */
export const getUserPostFeedNameWithQuery = ({
  state,
  userId,
  listType,
} = {}) => {
  if (!userId) return null;

  const feedListType = listType || ProfileListType.POST;
  const configKey = PROFILE_FEED_REMOTE_CONFIG_KEY_MAP[feedListType];
  if (!configKey) return null;

  const outboxFeedName = getFeedName(state, configKey, '{USER_ID}', userId);
  const { feedNameWithQuery } = getSelectedQueryOptionObject(
    state,
    outboxFeedName,
    getSelectedFeedQueryOptions(state, configKey, undefined),
    getRemoteConfigData(
      state,
      `${configKey}_${QueryPrefix[QueryType.SORT]}`
    )?.[0]
  );

  return feedNameWithQuery;
};

/**
 * Fetch feed total count via HEAD request
 * This is a reusable function that can work with any feed name
 * @param {string} feedNameWithQuery - Feed name with query parameters
 * @param {Object} httpProxyHeaders - HTTP proxy headers for SSR
 * @returns {Promise<number>} Total count number
 */
export const fetchFeedTotalCount = async ({
  feedNameWithQuery,
  httpProxyHeaders = {},
} = {}) => {
  try {
    const url = getResourceUrl({ endpoint: `/feeds/${feedNameWithQuery}` });
    const response = await fetch(url.href, {
      method: 'HEAD',
      headers: httpProxyHeaders,
    });

    // Check the x-total-count header
    const totalCount = parseInt(response.headers.get('x-total-count'), 10);
    return totalCount;
  } catch (error) {
    // If there's an error, return 0 to indicate no content
    // This ensures proper noindex behavior on error
    return 0;
  }
};

export default fetchFeedTotalCount;
